/**
 * DashTile — the ONE tile frame for the redesigned /main dashboard
 * (quick-261104-ia3, John 2026-11-04: "every tile needs to be tight and visually
 * equal and appealing. a civility and uniformity").
 *
 * Fixed anatomy, fixed heights (sm 220 · md 300 · lg 380 · xl 460), so tiles in a
 * row always line up:
 *   title · ⓘ plain-English hover · optional control (toggle)   ← header row
 *   headline number · sub-line / change chip                    ← headline row
 *   body (chart / list) — fills the remaining height            ← flex-1
 *   one-line footnote (source / basis)                          ← footer
 */
import { useId, useState, type ReactNode } from 'react';

export type TileSize = 'sm' | 'md' | 'lg' | 'xl';

const HEIGHT: Record<TileSize, string> = {
  sm: 'h-[220px]',
  md: 'h-[300px]',
  lg: 'h-[380px]',
  xl: 'h-[460px]',
};

export interface DashTileProps {
  title: string;
  /** Plain-English "what am I looking at" — CEO-digestible, 1–3 short sentences. */
  info: string;
  headline?: ReactNode;
  sub?: ReactNode;
  control?: ReactNode;
  footer?: ReactNode;
  size?: TileSize;
  children?: ReactNode;
  testId?: string;
}

export function DashTile({
  title,
  info,
  headline,
  sub,
  control,
  footer,
  size = 'md',
  children,
  testId,
}: DashTileProps): React.JSX.Element {
  return (
    <section
      className={`flex ${HEIGHT[size]} min-w-0 flex-col rounded-[14px] border border-ink-100 bg-white p-4 shadow-[0_1px_2px_rgba(16,28,45,0.04)]`}
      data-testid={testId}
      aria-label={title}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <h3 className="truncate text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">{title}</h3>
          <InfoHover text={info} label={`What is ${title}?`} />
        </div>
        {control ? <div className="shrink-0">{control}</div> : null}
      </header>
      {headline != null || sub != null ? (
        <div className="mt-1 flex min-h-[2.25rem] items-baseline gap-2">
          {headline != null ? (
            <span className="text-[1.6rem] font-semibold leading-none tracking-tight text-ink-900 tabular-nums">{headline}</span>
          ) : null}
          {sub != null ? <span className="min-w-0 truncate text-xs text-ink-500">{sub}</span> : null}
        </div>
      ) : null}
      <div className="mt-2 min-h-0 flex-1">{children}</div>
      {footer != null ? <footer className="mt-1.5 truncate text-[0.68rem] text-ink-400">{footer}</footer> : null}
    </section>
  );
}

/** ⓘ button that shows a short plain-English explanation on hover / focus. */
export function InfoHover({ text, label }: { text: string; label: string }): React.JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        onMouseEnter={() => {
          setOpen(true);
        }}
        onMouseLeave={() => {
          setOpen(false);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-ink-200 text-[0.6rem] font-semibold text-ink-500 hover:border-brand-500 hover:text-brand-600"
      >
        i
      </button>
      <span
        id={id}
        role="tooltip"
        className={`absolute left-0 top-6 z-30 w-72 rounded-lg border border-ink-200 bg-white p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-ink-700 shadow-lg ${open ? 'block' : 'hidden'}`}
      >
        {text}
      </span>
    </span>
  );
}

/** Small change chip: green up / red down (or inverted when lower is better). Icon + text, never colour alone. */
export function ChangeChip({
  text,
  dir,
  lowerIsBetter = false,
  suffix,
}: {
  text: string;
  dir: 'up' | 'down' | 'flat';
  lowerIsBetter?: boolean;
  suffix?: string;
}): React.JSX.Element {
  const good = dir === 'flat' ? null : (dir === 'up') !== lowerIsBetter;
  const cls =
    good === null ? 'bg-ink-100 text-ink-600' : good ? 'bg-good-bg text-good-text' : 'bg-bad-bg text-bad-text';
  const icon = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.68rem] font-semibold ${cls}`}>
      <span aria-hidden>{icon}</span>
      {text}
      {suffix ? <span className="font-normal opacity-80">{suffix}</span> : null}
    </span>
  );
}

/** Empty / loading state that keeps the tile's height. */
export function TileEmpty({ text }: { text: string }): React.JSX.Element {
  return (
    <div className="flex h-full items-center justify-center rounded-lg bg-ink-50 px-4 text-center text-xs text-ink-500">
      {text}
    </div>
  );
}

/** HTML legend row (identity never by colour alone — always a label). */
export function Legend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }): React.JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.68rem] text-ink-600">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-2 w-2.5 rounded-[2px]"
            style={i.dashed ? { border: `1.5px dashed ${i.color}` } : { background: i.color }}
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}
